/**
 * AnyClip — 메인 App 컴포넌트
 *
 * 초기화 순서:
 * 1. Telegram WebApp SDK 초기화 (ready + expand)
 * 2. 테마(dark/light) 자동 적용
 * 3. 유저 정보 전역 상태에 저장
 * 4. 레이아웃 조립
 */

import React, { useEffect } from 'react';
import { AppProvider, useApp, ACTIONS } from './context/AppContext';
import { useI18n } from './context/I18nContext';
import DownloadForm from './components/DownloadForm';
import AdBanner from './components/AdBanner';
import ReferralModal from './components/ReferralModal';
import StatusDisplay from './components/StatusDisplay';
import './App.css';

// ── 내부 앱 (Provider 내부에서 상태 접근) ────────────────────
function AppInner() {
  const { state, dispatch } = useApp();
  const { t } = useI18n();
  const { status } = state;

  // CPA center 배너: 로딩 중이거나 성공 상태일 때 표시
  const showCenterAd = status === 'loading' || status === 'success';

  // 텔레그램 네이티브 뒤로가기 버튼 연동
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg || !tg.BackButton) return;

    if (status !== 'idle') {
      tg.BackButton.show();
      const handleBack = () => {
        dispatch({ type: ACTIONS.RESET });
      };
      tg.BackButton.onClick(handleBack);
      
      return () => {
        tg.BackButton.offClick(handleBack);
        tg.BackButton.hide();
      };
    } else {
      tg.BackButton.hide();
    }
  }, [status, dispatch]);

  // 앱 시작 시 텔레그램 유저 정보 state.user에 저장 (필수!)
  // 이 없으면 telegramId가 항상 'dev_user_001'로 fallback됨
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    const user = tg?.initDataUnsafe?.user;
    if (user) {
      dispatch({ type: ACTIONS.SET_USER, payload: user });
    }
  }, [dispatch]);

  // 앱 시작 시 DB에서 실제 티켓 수 동기화
  useEffect(() => {
    const syncUserStatus = async () => {
      try {
        const tg = window.Telegram?.WebApp;
        const telegramId = tg?.initDataUnsafe?.user?.id || 'dev_user_001';
        const initData = tg?.initData || '';
        const BASE = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${BASE}/api/user/status?telegramId=${telegramId}`, {
          headers: { 'x-init-data': initData },
        });
        if (res.ok) {
          const data = await res.json();
          if (typeof data.tickets === 'number') {
            dispatch({ type: ACTIONS.SET_TICKETS, payload: { tickets: data.tickets, downloadCount: data.downloadCount || 0 } });
          }
        }
      } catch (e) {
        // 동기화 실패 시 무시 (오프라인 등)
      }
    };
    syncUserStatus();
  }, [dispatch]);

  return (
    <div className="app-container">
      {/* 상단 헤더 */}
      <header className="app-header">
        <div className="app-logo">
          <span className="app-logo-icon">🎬</span>
          <span className="app-logo-text">AnyClip</span>
        </div>
        <span className="app-tagline">{t('app_tagline')}</span>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="app-main">
        {/* idle, error, referral_locked 상태: 폼 표시 (모달 뒤 배경 유지) */}
        {(status === 'idle' || status === 'error' || status === 'referral_locked') && (
          <>
            <div className="hero-text">
              <h1 className="main-title">
                {t('title')}<br />
                <span className="gradient-text">Fast</span>
              </h1>
              <p className="main-sub">{t('subtitle')}</p>
            </div>
            <DownloadForm />
          </>
        )}

        {/* 로딩 / 완료 / 에러 상태 표시 */}
        <StatusDisplay />
      </main>

      {/* CPA 광고: 로딩 중 + 완료 시 중앙 배너 */}
      <AdBanner position="center" visible={showCenterAd} />

      {/* CPA 광고: 하단 고정 배너 (항상) */}
      <AdBanner position="bottom" visible={true} />

      {/* 레퍼럴 락 모달 */}
      <ReferralModal />
    </div>
  );
}

// ── 루트 App ────────────────────────────────────────────────
function App() {
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    // 1. TMA 준비 완료 신호
    tg.ready();

    // 2. 전체 화면 확장
    tg.expand();

    // 3. 테마 적용 (다크/라이트 자동 감지)
    const scheme = tg.colorScheme || 'dark';
    document.documentElement.setAttribute('data-theme', scheme);
    if (tg.themeParams?.bg_color) {
      document.documentElement.style.setProperty('--tg-bg', tg.themeParams.bg_color);
    }

    // 4. 광고 스크립트 동적 로드 (로딩 화면 멈춤 방지)
    const loadAdScript = (src, dataZone, dataSdk) => {
      if (!document.querySelector(`script[src="${src}"]`)) {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        if (dataZone) script.setAttribute('data-zone', dataZone);
        if (dataSdk) script.setAttribute('data-sdk', dataSdk);
        document.body.appendChild(script);
      }
    };

    loadAdScript('https://sad.adsgram.ai/js/sad.min.js');
    // Monetag SDK — Rewarded Popup('pop') 방식 (Interstitial보다 안전)
    loadAdScript('https://libtl.com/sdk.js', '11192388', 'show_11192388');

    console.log('[TMA] 초기화 완료 | 유저:', tg.initDataUnsafe?.user?.id);
  }, []);

  return (
    <AppProvider>
      <AppInner />
    </AppProvider>
  );
}

export default App;
